import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Move-out clean, empty 2 bed / 1 bath apartment, about 950 sq ft, all rooms', amount: '$240' },
  { item: 'Inside kitchen cabinets, drawers and pantry shelves', amount: '$55' },
  { item: 'Inside oven, range hood filter soaked and degreased', amount: '$45' },
  { item: 'Optional: wipe blinds in all rooms, 7 windows', amount: '$40' },
];

const faq = [
  {
    q: 'Can I use this template for standard, deep and move-out cleans?',
    a: 'Yes. The task grid has a column for each type. Tick the tasks that apply to this job and delete the columns you do not need before you send it.',
  },
  {
    q: 'What is the most important part of a cleaning quote?',
    a: 'The task list. Most complaints come from a customer expecting something that was never included, such as inside cabinets or baseboards. A written list settles it.',
  },
  {
    q: 'Should a cleaning quote include a lockout fee?',
    a: 'If you charge one, yes: put it in the access section so the customer sees it before accepting. The template leaves the amount blank for you to choose.',
  },
  {
    q: 'Can my customer accept this online?',
    a: 'If you build it in Scopie, the quote is emailed to your customer as a link. They can tick extras such as blinds or the fridge and accept it on their phone.',
  },
];

const page: TemplatePage = {
  slug: 'house-cleaning-quote-template',
  path: '/templates/house-cleaning-quote-template',
  primaryKeyword: 'house cleaning quote template',
  secondaryKeywords: ['cleaning estimate template', 'move out cleaning quote template', 'deep cleaning quote template'],
  title: 'Free House Cleaning Quote Template (PDF) | Scopie', // 49
  metaDescription:
    'A free house cleaning quote template with a task grid for standard, deep and move-out cleans, extras, access and lockout terms, and a filled example.', // 149
  h1: 'House cleaning quote template',
  answerFirst:
    'A house cleaning quote should spell out which tasks are included in each room, which extras cost more, and how you get in. This free template gives you a task grid for standard, deep and move-out cleans, plus access, lockout and cancellation lines, with a filled-in move-out example to copy.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'document',
      id: 'template',
      h2: 'The cleaning quote',
      intro: 'Tick the tasks in the column that matches the clean, and fill in the brackets.',
      pdfName: 'house-cleaning-quote-template',
      sections: [
        { heading: 'Cleaning company', lines: ['[Business name] · [Phone] · [Email]'] },
        { heading: 'Client and home', lines: ['[Client name] · [Address]', '[Bedrooms] bed / [bathrooms] bath · about [sq ft] · pets [yes / no]', 'Quote [number] · [date] · valid until [date]'] },
        {
          heading: 'Tasks (standard / deep / move-out)',
          lines: [
            'Kitchen: counters, sink, appliance fronts, floor [S D M] · cabinet fronts and backsplash degreased [D M] · inside cabinets [M]',
            'Bathrooms: toilet, tub or shower, sink, mirror, floor [S D M] · grout scrubbed, fan cover [D M]',
            'Bedrooms and living areas: dust reachable surfaces, vacuum or mop [S D M] · baseboards, door frames, switches [D M]',
            'Whole home: trash out, beds made if linens are left [S D] · closets and shelves wiped [M]',
            'Price for this clean: [amount]',
          ],
        },
        { heading: 'Extras', lines: ['Inside oven [price] · inside refrigerator [price] · inside windows [price] · blinds [price] · laundry [price]'] },
        {
          heading: 'Access and supplies',
          lines: ['Entry: [key / code / client home] · parking: [details]', 'Supplies and equipment: [we bring our own / client provides]', 'Lockout: if we cannot get in at the scheduled time, [your charge]'],
        },
        { heading: 'Terms', lines: ['Cancellation: [notice period and charge].', 'Deposit: [amount or none].', 'Heavy build-up beyond what is described may need extra time, agreed before we continue.'] },
        { heading: 'Accepted', lines: ['[Client name] · [Signature] · [Date]'] },
      ],
    },
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'Example: an apartment move-out',
      label: 'Filled in for an empty two-bedroom apartment',
      lines: filled,
      total: '$380',
      totalLabel: 'Total with blinds',
      note: 'Illustrative prices only, not a rate guide.',
    },
    {
      type: 'list',
      id: 'before-sending',
      h2: 'Questions to ask before you fill it in',
      style: 'checklist',
      items: [
        { text: 'When was the home last cleaned professionally?' },
        { text: 'Are there pets, and do they shed heavily?' },
        { text: 'For move-outs: will power and water be on, and is the home empty?' },
        { text: 'Does a landlord or agent have their own checklist?' },
        { text: 'Who lets the team in, and where does the key go afterwards?' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Where cleaning quotes go wrong',
      style: 'mistakes',
      items: [
        { title: '"Deep clean" with no task list', text: 'Every customer imagines a different deep clean. List the tasks.' },
        { title: 'Extras done for free', text: 'Inside the oven and fridge are real time. Price them, even if you sometimes waive them.' },
        { title: 'No lockout line', text: 'A wasted trip is lost income unless your terms cover it.' },
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Cleaning quote template questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/cleaning', anchor: 'Scopie for cleaning businesses', status: 'live' },
    { href: '/templates/cancellation-policy', anchor: 'cancellation policy template', status: 'live' },
    { href: '/templates/commercial-cleaning-proposal-template', anchor: 'commercial cleaning proposal template', status: 'planned' },
  ],
  cta: {
    heading: 'Stop retyping the same task list',
    text: 'Save your cleaning tasks, extras and lockout terms in Scopie, then email each quote as a link the client can accept.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
